"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React from "react";
import { Link } from "@/components/common/Link";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Linkedin, Mail, Network, ShieldCheck } from "lucide-react";
const leaders = [{
  name: "Marcus Thorne",
  position: "Principal Consultant",
  bio: "Architect of high-level digital frameworks with two decades of experience in organizational optimization and systems engineering.",
  competencies: ["Systems Architecture", "Strategic Advisory", "Digital Workflow"],
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1619657/img/team_alice_johnson.png",
  linkedin: "https://linkedin.com",
  icon: Network
}, {
  name: "Elena Vance",
  position: "Strategic Advisor",
  bio: "Expert in technical oversight and infrastructure modernization, specializing in streamlining internal professional service delivery.",
  competencies: ["Technical Oversight", "Efficiency Scaling", "Modernization"],
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1619657/img/team_david_lee.png",
  linkedin: "https://linkedin.com",
  icon: ShieldCheck
}];
export default function OurStrategicLeaders() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="149744" id="principal-leadership" className="relative bg-background py-24 overflow-hidden" ref={sectionRef}>
      {}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,var(--color-border)_1px,transparent_1px),linear-gradient(to_bottom,var(--color-border)_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)] opacity-20 pointer-events-none" />

      <div className="container mx-auto px-4 relative z-10">
        <div className={`text-center max-w-3xl mx-auto mb-16 transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="inline-block mb-4 px-3 py-1 border border-primary/30 rounded-sm bg-primary/5">
            <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-primary">Executive Oversight</span>
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-foreground tracking-tighter mb-4">
            Principal Leadership
          </h2>
          <p className="text-lg text-muted-foreground font-sans">
            Specialized advisors dedicated to the intersection of modern technology 
            and organizational efficiency for high-level professional services.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {leaders.map((leader, index) => {
          const Icon = leader.icon;
          return <div key={leader.name} data-index={index} className={`transition-all duration-700 ease-out delay-${index * 100 + 100} ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
                <Card className="h-full border-border/40 shadow-xl hover:shadow-primary/5 transition-all duration-500 bg-card/40 backdrop-blur-md group rounded-xl overflow-hidden">
                  <HoverCard openDelay={200} closeDelay={100}>
                    <HoverCardTrigger asChild>
                      <div className="cursor-pointer focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary">
                        <CardHeader className="flex flex-row items-start gap-5 pb-4 relative overflow-hidden">
                          {}
                          <div className="absolute inset-0 bg-primary/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
                          
                          <SafeHydrate><Avatar className="size-24 border border-border/50 shadow-2xl ring-1 ring-border/20 group-hover:ring-primary/40 transition-all duration-500 relative z-10 rounded-lg">
                            <AvatarImage src={leader.image} alt={leader.name} className="object-cover grayscale group-hover:grayscale-0 transition-all duration-700" />
                            <AvatarFallback className="bg-muted text-muted-foreground text-xl font-mono">
                              {leader.name.split(" ").map(n => n[0]).join("")}
                            </AvatarFallback>
                          </Avatar></SafeHydrate>
                          
                          <div className="relative z-10 pt-2">
                            <CardTitle className="text-2xl font-bold text-foreground group-hover:text-primary transition-colors tracking-tight">
                              {leader.name}
                            </CardTitle>
                            <CardDescription className="text-primary font-mono text-xs uppercase tracking-widest mt-1.5 flex items-center gap-1.5">
                              <Icon className="size-3.5" />
                              {leader.position}
                            </CardDescription>
                          </div>
                        </CardHeader>
                      </div>
                    </HoverCardTrigger>
                    <HoverCardContent align="start" sideOffset={-10} className="w-72 p-4 flex flex-col gap-4 shadow-2xl border-border/80 bg-popover/98 backdrop-blur-xl rounded-lg">
                      <div className="space-y-1">
                        <h4 className="text-sm font-bold text-foreground">
                          Consult with {leader.name.split(" ")[0]}
                        </h4>
                        <p className="text-xs text-muted-foreground leading-relaxed">
                          Available for technological optimization audits and strategic advisory sessions.
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button variant="outline" size="sm" className="flex-1 bg-background border-border/60 hover:bg-muted rounded-sm" asChild>
                          <Link to={leader.linkedin} newTab>
                            <Linkedin className="size-3.5 mr-2 text-primary" />
                            LinkedIn
                          </Link>
                        </Button>
                        <Button size="sm" className="flex-1 rounded-sm shadow-[0_0_15px_rgba(0,255,255,0.15)]" asChild>
                          <Link to="/StrategicInquiriesPage">
                            <Mail className="size-3.5 mr-2" />
                            Inquiry
                          </Link>
                        </Button>
                      </div>
                    </HoverCardContent>
                  </HoverCard>

                  <CardContent className="relative z-10 pt-2">
                    <p className="text-muted-foreground text-sm leading-relaxed mb-6 min-h-[3rem] font-sans">
                      {leader.bio}
                    </p>
                    <div className="space-y-3">
                      <h4 className="text-[10px] font-mono font-bold text-foreground/70 uppercase tracking-[0.2em] flex items-center gap-2">
                        <span className="h-px w-6 bg-primary/40 rounded-full"></span>
                        Technical Metadata
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {leader.competencies.map((comp, i) => <Badge key={comp} data-index={i} variant="secondary" className="bg-secondary/20 text-primary hover:bg-primary/10 border border-primary/10 transition-all duration-300 font-mono text-[10px] rounded-sm">
                            {comp}
                          </Badge>)}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>;
        })}
        </div>
      </div>
    </section>;
}